import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useToast } from "@/hooks/use-toast"
import { commonService } from "@/services/common.service"
import type { SiteSetting } from "@/types/api"
import { Plus, Edit2, Trash2, Loader2 } from "lucide-react"

const settingFormSchema = z.object({
  key: z.string().min(1, "Ключ обязателен"),
  value: z.string().min(1, "Значение обязательно"),
  type: z.enum(["string", "json", "url", "text", "number", "boolean"]),
})

export default function SiteSettingsPage() {
  const { toast } = useToast()
  const [settings, setSettings] = useState<SiteSetting[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [editSetting, setEditSetting] = useState<SiteSetting | null>(null)
  const [deleteSetting, setDeleteSetting] = useState<SiteSetting | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const form = useForm<z.infer<typeof settingFormSchema>>({
    resolver: zodResolver(settingFormSchema),
    defaultValues: {
      key: "",
      value: "",
      type: "string",
    },
  })

  const loadSettings = async () => {
    setIsLoading(true)
    try {
      const data = await commonService.getSiteSettings()
      setSettings(data)
    } catch (error) {
      toast({
        title: "Ошибка",
        description: "Не удалось загрузить настройки сайта",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadSettings()
  }, [])

  const onSubmit = async (values: z.infer<typeof settingFormSchema>) => {
    setIsSaving(true)
    try {
      if (editSetting) {
        await commonService.updateSiteSetting(editSetting.key, values)
        toast({
          title: "Успешно",
          description: "Настройка обновлена",
        })
      } else {
        await commonService.createSiteSetting(values)
        toast({
          title: "Успешно",
          description: "Настройка создана",
        })
      }
      setIsDialogOpen(false)
      form.reset()
      setEditSetting(null)
      loadSettings()
    } catch (error: any) {
      toast({
        title: "Ошибка",
        description: error?.data?.key?.[0] || `Не удалось ${editSetting ? "обновить" : "создать"} настройку`,
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleEdit = (setting: SiteSetting) => {
    setEditSetting(setting)
    form.reset({
      key: setting.key,
      value: setting.value,
      type: setting.type,
    })
    setIsDialogOpen(true)
  }

  const handleCreate = () => {
    setEditSetting(null)
    form.reset({
      key: "",
      value: "",
      type: "string",
    })
    setIsDialogOpen(true)
  }

  const handleDelete = async () => {
    if (!deleteSetting) return

    try {
      await commonService.deleteSiteSetting(deleteSetting.key)
      toast({
        title: "Успешно",
        description: "Настройка удалена",
      })
      setDeleteSetting(null)
      loadSettings()
    } catch (error) {
      toast({
        title: "Ошибка",
        description: "Не удалось удалить настройку",
        variant: "destructive",
      })
    }
  }

  const renderValue = (setting: SiteSetting) => {
    if (setting.type === "json") {
      try {
        return <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">{JSON.stringify(JSON.parse(setting.value), null, 2)}</pre>
      } catch {
        return <span className="text-xs text-red-500">Неверный JSON</span>
      }
    }

    if (setting.value.length > 100) {
      return <span className="text-sm text-gray-600">{setting.value.substring(0, 100)}...</span>
    }

    return <span className="text-sm text-gray-900">{setting.value}</span>
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Настройки сайта</h1>
          <p className="text-sm text-gray-500">
            Управление динамическими настройками сайта
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить настройку
        </Button>
      </div>

      <div className="grid gap-4">
        {settings.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              Настройки не найдены. Нажмите "Добавить настройку" для создания.
            </CardContent>
          </Card>
        ) : (
          settings.map((setting) => (
            <Card key={setting.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{setting.key}</CardTitle>
                    <CardDescription>
                      Тип: {setting.type} • Обновлено: {new Date(setting.updated_at).toLocaleString("ru-RU")}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(setting)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => setDeleteSetting(setting)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {renderValue(setting)}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editSetting ? "Редактировать настройку" : "Создать новую настройку"}
            </DialogTitle>
            <DialogDescription>
              {editSetting
                ? "Обновите значение настройки ниже"
                : "Введите данные для новой настройки сайта"}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ключ</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="footer_text"
                        disabled={!!editSetting}
                      />
                    </FormControl>
                    <FormDescription>
                      Уникальный идентификатор настройки (не может быть изменен после создания)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Тип</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Выберите тип" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="string">String (Строка)</SelectItem>
                        <SelectItem value="text">Text (Текст)</SelectItem>
                        <SelectItem value="url">URL (Ссылка)</SelectItem>
                        <SelectItem value="json">JSON (Объект)</SelectItem>
                        <SelectItem value="number">Number (Число)</SelectItem>
                        <SelectItem value="boolean">Boolean (Да/Нет)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Тип данных для значения настройки
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Значение</FormLabel>
                    <FormControl>
                      {form.watch("type") === "text" || form.watch("type") === "json" ? (
                        <Textarea
                          {...field}
                          placeholder={
                            form.watch("type") === "json"
                              ? '{"key": "value"}'
                              : "Введите текст..."
                          }
                          className="min-h-[150px] font-mono text-sm"
                        />
                      ) : (
                        <Input
                          {...field}
                          placeholder="Введите значение..."
                          type={form.watch("type") === "number" ? "number" : "text"}
                        />
                      )}
                    </FormControl>
                    <FormDescription>
                      {form.watch("type") === "json" && "Введите валидный JSON"}
                      {form.watch("type") === "url" && "Введите полный URL (например, https://example.com)"}
                      {form.watch("type") === "boolean" && "Введите true или false"}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false)
                    form.reset()
                    setEditSetting(null)
                  }}
                >
                  Отмена
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Сохранение...
                    </>
                  ) : (
                    editSetting ? "Обновить" : "Создать"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteSetting} onOpenChange={(open) => !open && setDeleteSetting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Вы уверены?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Настройка "{deleteSetting?.key}" будет удалена навсегда.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
